// helper functions
const createCookie = (
  name: string,
  value: string,
  days: number,
  secure: boolean = false,
  httpOnly: boolean = false
) => {
  let expires = ''
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = '; expires=' + date.toUTCString()
  }
  const secureFlag = secure ? '; secure' : ''
  const httpOnlyFlag = httpOnly ? '; HttpOnly' : ''
  document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/${secureFlag}${httpOnlyFlag}`
}

const readCookie = (name: string): string | undefined => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return decodeURIComponent(parts.pop()?.split(';').shift() || '')
  return undefined
}

const TEST_VALUE = Math.random().toString(10).slice(2)

export interface StorageDriver {
  enabled: boolean
  get(key: string): string | undefined
  set(key: string, value: string): void
  remove(key: string): void
}

export type CookieDriver = {
  enabled: boolean
  get(key: string): string | undefined
  set(key: string, value: string, days?: number, secure?: boolean, httpOnly?: boolean): void
}

function isLocalStorageEnabled(): boolean {
  try {
    localStorage.setItem('__is_enabled__', TEST_VALUE)
    return localStorage.getItem('__is_enabled__') === TEST_VALUE
  } catch {
    return false
  }
}

function isSessionStorageEnabled(): boolean {
  try {
    sessionStorage.setItem('__is_enabled__', TEST_VALUE)
    return sessionStorage.getItem('__is_enabled__') === TEST_VALUE
  } catch {
    return false
  }
}

function isCookieEnabled(): boolean {
  try {
    createCookie('__is_enabled__', TEST_VALUE, 1)
    return readCookie('__is_enabled__') === TEST_VALUE
  } catch {
    return false
  }
}

const localStorageEnabled = isLocalStorageEnabled()
const localDriver: StorageDriver = {
  enabled: localStorageEnabled,
  get(key) {
    if (!localStorageEnabled) return undefined
    try {
      return localStorage.getItem(key) ?? undefined
    } catch {
      return undefined
    }
  },
  set(key, value) {
    if (!localStorageEnabled) return
    try {
      localStorage.setItem(key, value)
    } catch {}
  },
  remove(key) {
    if (!localStorageEnabled) return
    try {
      localStorage.removeItem(key)
    } catch {}
  },
}

const sessionStorageEnabled = isSessionStorageEnabled()
export const sessionDriver: StorageDriver = {
  enabled: sessionStorageEnabled,
  get(key) {
    if (!sessionStorageEnabled) return undefined
    try {
      return sessionStorage.getItem(key) ?? undefined
    } catch {
      return undefined
    }
  },
  set(key, value) {
    if (!sessionStorageEnabled) return
    try {
      sessionStorage.setItem(key, value)
    } catch {}
  },
  remove(key) {
    if (!sessionStorageEnabled) return
    try {
      sessionStorage.removeItem(key)
    } catch {}
  },
}

const cookiesEnabled = isCookieEnabled()
export const cookieDriver: CookieDriver = {
  enabled: cookiesEnabled,
  get(key) {
    if (!cookiesEnabled) return undefined
    try {
      return readCookie(key)
    } catch {
      return undefined
    }
  },
  set(key, value, days = 1, secure = false, httpOnly = false) {
    if (!cookiesEnabled) return
    try {
      createCookie(key, value, days, secure, httpOnly)
    } catch {}
  },
}

export const storage = {
  local: localDriver,
  session: sessionDriver,
  cookie: cookieDriver,
}
